import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';

import { useEmojiLabel } from '../features/emoji/useEmojiLabel';
import { EmojiPicker } from '../features/profiles/EmojiPicker';
import {
  addTask,
  copyTaskToRoutine,
  duplicateRoutine,
  nextTaskSortIndex,
  reorderTask,
  removeRoutine,
  removeRoutineFromLibrary,
  removeTask,
  replaceRoutine,
  updateRoutine,
  updateTask,
} from '../features/profiles/routineMutations';
import { buildShareUrl } from '../features/share/encodeShare';
import { ShareTooLargeError } from '../features/share/types';
import { bySortIndex } from '../lib/sortIndex';
import type { Routine, Task } from '../lib/types';
import { uid } from '../lib/uid';
import { useSyncedStore } from '../store/synced';

// Routine editor per plan/03 §Routine editor: emoji + name + task list
// with touch-friendly drag-and-drop reorder. Add/edit/delete tasks inline.
//
// Reachable from two entry points:
//   - /settings/profiles/:id/list/:listId — nav back / delete-return to
//     that profile; delete unassigns from that profile only (library
//     entry is pruned iff no other profile still references it).
//   - /settings/routines/:listId (tasks/48) — no source profile; nav
//     back / delete-return to the library hub; delete drops the routine
//     from every profile that had it.
export function RoutineEditorPage() {
  const { id: profileId, listId } = useParams<{ id?: string; listId: string }>();
  const navigate = useNavigate();
  const tree = useSyncedStore((s) => s.tree);
  const setTree = useSyncedStore((s) => s.setTree);

  const profile = tree?.profiles.find((p) => p.id === profileId);
  // v3: routines live in the library. The route is still nested under a
  // profile so the "back" nav lands correctly, but the routine itself is
  // looked up by id.
  const routine = tree?.routines.find((r) => r.id === listId);

  const nav = useMemo(() => {
    if (profileId) {
      return {
        backHref: `/settings/profiles/${profileId}`,
        backLabel: 'Takaisin profiilieditoriin',
        afterDeleteHref: `/settings/profiles/${profileId}`,
        afterDuplicateHref: (newId: string) => `/settings/profiles/${profileId}/list/${newId}`,
      };
    }
    return {
      backHref: '/settings/routines',
      backLabel: 'Takaisin tehtävälistoihin',
      afterDeleteHref: '/settings/routines',
      afterDuplicateHref: (newId: string) => `/settings/routines/${newId}`,
    };
  }, [profileId]);

  const [routineEmojiPicker, setRoutineEmojiPicker] = useState(false);
  const [taskEmojiPicker, setTaskEmojiPicker] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [shareFallback, setShareFallback] = useState<string | null>(null);
  const [shareTooLarge, setShareTooLarge] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [copyTaskId, setCopyTaskId] = useState<string | null>(null);
  const [copyTarget, setCopyTarget] = useState<string | null>(null);
  const [copiedStatus, setCopiedStatus] = useState<{ taskId: string; targetName: string } | null>(
    null,
  );

  // Fade the "Tehtävä kopioitu: <name>" indicator after 2s. Cleared on
  // unmount / next copy so the indicator never lingers past its window.
  useEffect(() => {
    if (!copiedStatus) return undefined;
    const timer = globalThis.setTimeout(() => setCopiedStatus(null), 2000);
    return () => globalThis.clearTimeout(timer);
  }, [copiedStatus]);
  // Snapshot the routine on first render so the revert button has somewhere
  // to go. A new editing session (mount) starts a new snapshot.
  const [snapshot] = useState<Routine | null>(() => routine ?? null);
  const dirty = useMemo(() => {
    if (!snapshot || !routine) return false;
    return JSON.stringify(snapshot) !== JSON.stringify(routine);
  }, [snapshot, routine]);

  const routineEmojiLabel = useEmojiLabel(routine?.emoji ?? '');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Profile-scoped entry with an unknown profile: fall back to /settings.
  // Library-scoped entry (profileId absent) is fine even without a profile.
  if (!tree) return <Navigate to="/settings" replace />;
  if (profileId && !profile) return <Navigate to="/settings" replace />;
  // Routine missing: land on the parent that opened the editor. Profile
  // entry → the profile editor (usually the user just deleted the routine
  // from this very page); library entry → the hub.
  if (!routine) return <Navigate to={nav.afterDeleteHref} replace />;

  const tasks = routine.tasks.slice().sort(bySortIndex);
  const activeTask = activeId ? (tasks.find((t) => t.id === activeId) ?? null) : null;
  const copySourceTask = copyTaskId ? (tasks.find((t) => t.id === copyTaskId) ?? null) : null;
  // Target list: current routine at the top with a "(sama lista)" hint,
  // then every other library routine in sortIndex order. When the library
  // has a single routine the "other" list is empty — that's fine, the
  // current-routine row alone lets parents clone-then-tweak in place.
  const otherRoutines = tree.routines
    .filter((r) => r.id !== routine.id)
    .slice()
    .sort(bySortIndex);

  const onName = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTree(updateRoutine(tree, routine.id, { name: e.target.value }));
  };

  const onPickRoutineEmoji = (emoji: string) => {
    setTree(updateRoutine(tree, routine.id, { emoji }));
    setRoutineEmojiPicker(false);
  };

  const onPickTaskEmoji = (emoji: string) => {
    if (!taskEmojiPicker) return;
    setTree(updateTask(tree, routine.id, taskEmojiPicker, { emoji }));
    setTaskEmojiPicker(null);
  };

  const onAddTask = () => {
    const ts = new Date().toISOString();
    const t: Task = {
      id: uid(),
      name: '',
      emoji: '✨',
      tag: null,
      sortIndex: nextTaskSortIndex(routine),
      createdAt: ts,
      updatedAt: ts,
    };
    setTree(addTask(tree, routine.id, t, ts));
  };

  const onTaskField = (taskId: string, patch: Partial<Pick<Task, 'name' | 'tag'>>) => {
    setTree(updateTask(tree, routine.id, taskId, patch));
  };

  const onTaskRemove = (taskId: string) => {
    setTree(removeTask(tree, routine.id, taskId));
  };

  const onOpenCopyModal = (taskId: string) => {
    setCopyTaskId(taskId);
    setCopyTarget(null);
  };

  const onCancelCopy = () => {
    setCopyTaskId(null);
    setCopyTarget(null);
  };

  const onConfirmCopy = () => {
    if (!copyTaskId || !copyTarget) return;
    const target = tree.routines.find((r) => r.id === copyTarget);
    if (!target) return;
    setTree(copyTaskToRoutine(tree, routine.id, copyTaskId, copyTarget));
    setCopiedStatus({ taskId: copyTaskId, targetName: target.name });
    setCopyTaskId(null);
    setCopyTarget(null);
  };

  const onDuplicateRoutine = () => {
    const { tree: next, newRoutineId } = duplicateRoutine(tree, routine.id);
    setTree(next);
    navigate(nav.afterDuplicateHref(newRoutineId));
  };

  const onDeleteRoutine = () => {
    // Profile entry: unlink from this profile and (if no other profile
    // still references it) prune the library entry. Library entry: drop
    // the routine from every profile that had it and from the library.
    if (profile) {
      setTree(removeRoutine(tree, profile.id, routine.id));
    } else {
      setTree(removeRoutineFromLibrary(tree, routine.id));
    }
    navigate(nav.afterDeleteHref, { replace: true });
  };

  const onRevert = () => {
    if (!snapshot) return;
    setTree(replaceRoutine(tree, snapshot));
  };

  const onDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = tasks.findIndex((t) => t.id === active.id);
    const newIndex = tasks.findIndex((t) => t.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    setTree(reorderTask(tree, routine.id, String(active.id), newIndex));
  };

  const onShare = async () => {
    let url: string;
    try {
      url = buildShareUrl(routine);
    } catch (e) {
      if (e instanceof ShareTooLargeError) {
        setShareTooLarge(true);
        return;
      }
      throw e;
    }
    try {
      await navigator.clipboard.writeText(url);
      setShareFallback(null);
    } catch {
      // iOS Safari without a user-gesture chain or non-https dev — fall
      // back to a textarea so the parent can long-press → copy.
      setShareFallback(url);
    }
  };

  return (
    <main className="settings">
      <Link to={nav.backHref} className="back-button" aria-label={nav.backLabel}>
        <span aria-hidden="true">←</span>
      </Link>
      <h1 className="settings-title">Rutiini</h1>

      <section className="settings-section">
        <h2 className="settings-section-title">Emoji ja nimi</h2>
        <div className="profile-edit-row">
          <button
            type="button"
            className="profile-edit-avatar"
            onClick={() => setRoutineEmojiPicker(true)}
            aria-label={`Vaihda rutiinin emoji (nyt: ${routineEmojiLabel})`}
          >
            <span aria-hidden="true">{routine.emoji}</span>
          </button>
          <input
            type="text"
            className="profile-edit-name"
            value={routine.name}
            onChange={onName}
            aria-label="Rutiinin nimi"
            placeholder="Esim. Aamu"
          />
        </div>
      </section>

      <section className="settings-section">
        <h2 className="settings-section-title">Tehtävät</h2>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={(e) => setActiveId(String(e.active.id))}
          onDragEnd={onDragEnd}
          onDragCancel={() => setActiveId(null)}
        >
          <SortableContext items={tasks.map((t) => t.id)} strategy={verticalListSortingStrategy}>
            <ul className="task-edit-list">
              {tasks.map((t, i) => (
                <TaskEditorRow
                  key={t.id}
                  task={t}
                  index={i}
                  isDragOverlay={false}
                  onPickEmoji={() => setTaskEmojiPicker(t.id)}
                  onField={(patch) => onTaskField(t.id, patch)}
                  onRemove={() => onTaskRemove(t.id)}
                  onCopy={() => onOpenCopyModal(t.id)}
                  copiedStatus={copiedStatus?.taskId === t.id ? copiedStatus.targetName : null}
                />
              ))}
            </ul>
          </SortableContext>
          <DragOverlay>
            {activeTask && (
              <TaskEditorRow
                task={activeTask}
                index={tasks.findIndex((t) => t.id === activeTask.id)}
                isDragOverlay={true}
                onPickEmoji={() => {}}
                onField={() => {}}
                onRemove={() => {}}
                onCopy={() => {}}
                copiedStatus={null}
              />
            )}
          </DragOverlay>
        </DndContext>
        <button type="button" className="settings-button" onClick={onAddTask}>
          + Lisää tehtävä
        </button>
      </section>

      <section className="settings-section">
        <h2 className="settings-section-title">Jaa</h2>
        <p className="settings-hint">
          Kopioi linkki, jonka voit lähettää toiselle perheelle. Vastaanottaja näkee esikatselun
          ennen tuontia.
        </p>
        <button type="button" className="settings-button" onClick={onShare}>
          Jaa rutiini
        </button>
        {shareTooLarge && (
          <p className="settings-error">
            Rutiini on liian iso linkkiin — lataa tiedostona tai poista tehtäviä.
          </p>
        )}
      </section>

      <section className="settings-section">
        <h2 className="settings-section-title">Toiminnot</h2>
        <p className="settings-hint">
          Kopio saa oman kirjasto­merkinnän ja päättyy tekstillä " (kopio)". Sitä ei liitetä
          automaattisesti kenellekään lapselle.
        </p>
        <button type="button" className="settings-button" onClick={onDuplicateRoutine}>
          Duplikoi rutiini
        </button>
      </section>

      <section className="settings-section">
        <h2 className="settings-section-title">Vaarallinen alue</h2>
        <p className="settings-hint">Rutiinin poistaminen pyyhkii sen tehtävät ja edistymisen.</p>
        <button
          type="button"
          className="settings-button settings-button-danger"
          onClick={() => setConfirmDelete(true)}
        >
          Poista rutiini
        </button>
      </section>

      {dirty && (
        <output className="revert-banner" data-testid="revert-banner">
          <span>Muutoksia tehty.</span>
          <button type="button" className="settings-button" onClick={onRevert}>
            Peruuta muutokset
          </button>
        </output>
      )}
      {routineEmojiPicker && (
        <EmojiPicker onPick={onPickRoutineEmoji} onCancel={() => setRoutineEmojiPicker(false)} />
      )}
      {taskEmojiPicker && (
        <EmojiPicker onPick={onPickTaskEmoji} onCancel={() => setTaskEmojiPicker(null)} />
      )}
      {shareFallback !== null && (
        <div className="confirm-modal" role="dialog" aria-label="Kopioi linkki">
          <button
            type="button"
            className="confirm-backdrop"
            aria-label="Sulje"
            onClick={() => setShareFallback(null)}
          />
          <div className="confirm-box share-fallback-modal">
            <h2>Kopioi linkki</h2>
            <p>Pidä sormea tekstikentässä ja valitse "Kopioi".</p>
            <textarea readOnly value={shareFallback} aria-label="Jaettava linkki" rows={4} />
            <div className="confirm-actions">
              <button
                type="button"
                className="settings-button"
                onClick={() => setShareFallback(null)}
              >
                Sulje
              </button>
            </div>
          </div>
        </div>
      )}
      {copyTaskId && copySourceTask && (
        <dialog open className="confirm-modal" aria-label="Kopioi tehtävä">
          <button
            type="button"
            className="confirm-backdrop"
            aria-label="Sulje"
            onClick={onCancelCopy}
          />
          <div className="confirm-box">
            <h2>Kopioi tehtävä</h2>
            <p className="settings-hint">
              <span aria-hidden="true">{copySourceTask.emoji}</span>{' '}
              {copySourceTask.name || 'tehtävä'}
            </p>
            <ul className="profile-grid">
              <li key={routine.id}>
                <button
                  type="button"
                  className="profile-tile"
                  aria-pressed={copyTarget === routine.id}
                  onClick={() => setCopyTarget(routine.id)}
                  aria-label={`Kopioi samaan rutiiniin: ${routine.name || 'tämä rutiini'}`}
                >
                  <span aria-hidden="true">{routine.emoji}</span>
                  <span>
                    {routine.name || 'Rutiini'} <em>(sama lista)</em>
                  </span>
                </button>
              </li>
              {otherRoutines.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    className="profile-tile"
                    aria-pressed={copyTarget === r.id}
                    onClick={() => setCopyTarget(r.id)}
                    aria-label={`Kopioi rutiiniin: ${r.name}`}
                  >
                    <span aria-hidden="true">{r.emoji}</span>
                    <span>{r.name}</span>
                  </button>
                </li>
              ))}
            </ul>
            <div className="confirm-actions">
              <button type="button" className="settings-button" onClick={onCancelCopy}>
                Peruuta
              </button>
              <button
                type="button"
                className="settings-button"
                onClick={onConfirmCopy}
                disabled={!copyTarget}
              >
                Kopioi
              </button>
            </div>
          </div>
        </dialog>
      )}
      {confirmDelete && (
        <div className="confirm-modal" role="dialog" aria-label="Vahvista poisto">
          <button
            type="button"
            className="confirm-backdrop"
            aria-label="Sulje"
            onClick={() => setConfirmDelete(false)}
          />
          <div className="confirm-box">
            <h2>Poista rutiini?</h2>
            <p>{routine.name || 'Tämä rutiini'} ja sen tehtävät poistetaan.</p>
            <div className="confirm-actions">
              <button
                type="button"
                className="settings-button"
                onClick={() => setConfirmDelete(false)}
              >
                Peruuta
              </button>
              <button
                type="button"
                className="settings-button settings-button-danger"
                onClick={onDeleteRoutine}
              >
                Poista
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

function TaskEditorRow({
  task,
  index,
  isDragOverlay,
  onPickEmoji,
  onField,
  onRemove,
  onCopy,
  copiedStatus,
}: {
  task: Task;
  index: number;
  isDragOverlay: boolean;
  onPickEmoji: () => void;
  onField: (patch: Partial<Pick<Task, 'name' | 'tag'>>) => void;
  onRemove: () => void;
  onCopy: () => void;
  copiedStatus: string | null;
}) {
  const emojiLabel = useEmojiLabel(task.emoji);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: task.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`task-edit-row${isDragging ? ' task-edit-row--dragging' : ''}${isDragOverlay ? ' task-edit-row--overlay' : ''}`}
    >
      <button
        type="button"
        className="task-edit-handle"
        aria-label={`Siirrä tehtävä: ${task.name || 'tehtävä'}`}
        {...attributes}
        {...listeners}
      >
        <span aria-hidden="true">⠿</span>
      </button>
      <button
        type="button"
        className="task-edit-emoji"
        onClick={onPickEmoji}
        aria-label={`Vaihda emoji: ${task.name || 'tehtävä'} (nyt: ${emojiLabel})`}
      >
        <span aria-hidden="true">{task.emoji}</span>
      </button>
      <div className="task-edit-fields">
        <input
          type="text"
          value={task.name}
          onChange={(e) => onField({ name: e.target.value })}
          placeholder="TEHTÄVÄ"
          aria-label={`Tehtävän nimi (${index + 1})`}
        />
        <input
          type="text"
          value={task.tag ?? ''}
          onChange={(e) => onField({ tag: e.target.value === '' ? null : e.target.value })}
          placeholder="Juhlinta (valinnainen)"
          aria-label={`Tehtävän juhlintarivi (${index + 1})`}
          className="task-edit-tag"
        />
      </div>
      <button
        type="button"
        onClick={onCopy}
        aria-label={`Kopioi tehtävä toiseen rutiiniin: ${task.name || 'tehtävä'}`}
        className="task-edit-copy"
      >
        <span aria-hidden="true">⧉</span>
      </button>
      <button
        type="button"
        onClick={onRemove}
        aria-label="Poista tehtävä"
        className="task-edit-delete"
      >
        ✕
      </button>
      {copiedStatus && (
        <output className="task-edit-copied" data-testid={`copied-status-${task.id}`}>
          Tehtävä kopioitu: {copiedStatus}
        </output>
      )}
    </li>
  );
}
